import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Loader2, Sparkles, ClipboardList, Target } from "lucide-react";
import AgentChatStream from "./AgentChatStream";

/**
 * Two-pane "chat window" layout:
 *   LEFT (narrow): prompt summary + pipeline stats — the user's original ask.
 *   RIGHT (wide): live-streaming agent reasoning, each in its own collapsible bubble.
 *
 * Follows the same convention as Claude / Emergent where the assistant's chain-of-thought
 * is exposed progressively as it works.
 */
export default function PipelineProgress({ pipelineStatus, inputSummary }) {
  const progress = pipelineStatus?.progress_percentage ?? 0;
  const questionCount = pipelineStatus?.question_count_so_far ?? 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6" data-testid="pipeline-progress">
      {/* LEFT — prompt summary + stats */}
      <aside className="space-y-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-primary" />
              Your brief
            </CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-gray-700 space-y-2">
            {inputSummary ? (
              <>
                <div>
                  <span className="font-semibold text-gray-900">Role:</span>{" "}
                  {inputSummary.role_title || "—"}
                </div>
                <div>
                  <span className="font-semibold text-gray-900">Seniority:</span>{" "}
                  {inputSummary.seniority_level || "—"}
                </div>
                <div>
                  <span className="font-semibold text-gray-900">Questions:</span>{" "}
                  {inputSummary.target_question_count}
                </div>
                <div>
                  <span className="font-semibold text-gray-900">Time limit:</span>{" "}
                  {inputSummary.time_limit_minutes} min
                </div>
                {inputSummary.focus_areas?.length > 0 && (
                  <div>
                    <span className="font-semibold text-gray-900 block mb-1">Focus:</span>
                    <div className="flex flex-wrap gap-1">
                      {inputSummary.focus_areas.map((f) => (
                        <span
                          key={f}
                          className="px-2 py-0.5 rounded bg-primary/10 text-primary text-[11px]"
                        >
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-gray-500 italic">No brief captured.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Target className="w-4 h-4 text-primary" />
              Live progress
            </CardTitle>
            <CardDescription className="text-xs">
              {progress < 100
                ? "Agents collaborating in real time"
                : "Pipeline complete"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Progress value={progress} className="h-2" />
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-600">Progress</span>
              <span className="font-semibold text-gray-900">{progress}%</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-600">Questions drafted</span>
              <span className="font-semibold text-gray-900">{questionCount}</span>
            </div>
          </CardContent>
        </Card>
      </aside>

      {/* RIGHT — agent chat stream */}
      <section>
        <div className="mb-4 flex items-center gap-2">
          {progress < 100 ? (
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
          ) : (
            <Sparkles className="w-5 h-5 text-primary" />
          )}
          <h2 className="text-lg font-semibold text-gray-900">
            {progress < 100 ? "AI agents working…" : "Agents finished"}
          </h2>
        </div>

        {pipelineStatus ? (
          <AgentChatStream pipelineStatus={pipelineStatus} />
        ) : (
          <Card>
            <CardContent className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
              <p className="text-gray-600">Initializing AI agents…</p>
              <p className="text-sm text-gray-500 mt-2">Connecting to the pipeline.</p>
            </CardContent>
          </Card>
        )}
      </section>
    </div>
  );
}
